'use client';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { type CSSProperties, type ReactNode, useState } from 'react';
import Latex from 'react-latex-next/dist';
import Questions from '@/components/exercise/questions';
import { useCourses } from '@/providers';
import type { Exercise } from '@/types';

function TabPanel({
  children,
  value,
  index,
  style,
  ...other
}: {
  children?: ReactNode;
  dir?: string;
  index: number;
  value: number;
  style?: CSSProperties;
}) {
  return (
    <Box
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      style={{ ...style, width: '100%' }}
      {...other}>
      {value === index && <>{children}</>}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

export default function ExerciseTabs({
  activeTabIndex,
  exercise,
}: {
  activeTabIndex?: number;
  exercise: Exercise;
}) {
  const { selectedCourse } = useCourses();
  const customTabIndex = !activeTabIndex ? 0 : activeTabIndex;
  const [tabIndex, setTabIndex] = useState<number>(customTabIndex);
  const [showReferences, setShowReferences] = useState(false);

  const onToggleShowReferences = () => {
    setShowReferences(!showReferences);
  };

  if (!selectedCourse || !exercise?.articles) return null;

  const { articles } = exercise;
  const { authors, paragraphs, references, title } = articles[0];

  const handleTabeChange = (newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: '100%',
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        '& > .MuiBox-root': { padding: 0 },
      }}>
      <Tabs
        value={tabIndex}
        onChange={(_event, value) => handleTabeChange(value)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        scrollButtons="auto"
        aria-label="Artikel und Fragen"
        centered
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          width: '100%',
          borderBottom: '1px solid #e8e8e8',
          zIndex: (theme) => theme.zIndex.appBar - 200,
          backgroundColor: (theme) => theme.palette.background.default,
        }}>
        <Tab label="Artikel" {...a11yProps(0)} />
        <Tab label="Fragen" {...a11yProps(1)} />
      </Tabs>
      <TabPanel value={tabIndex} index={0}>
        <Box
          sx={{
            width: '100%',
            my: 4,
            overflowX: 'auto',
          }}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {title}
          </Typography>
          {authors.map((author) => (
            <Typography key={author} sx={{ fontStyle: 'italic' }}>
              {author}
            </Typography>
          ))}
          <Box sx={{ my: 4 }}>
            {paragraphs.map(({ index, type, text }) => (
              <Box
                key={text}
                sx={{
                  mb: 2,
                  '& p': { mt: 0 },
                  '& h2': { textAlign: 'left !important' },
                }}>
                <Typography
                  variant="body2"
                  color="textSecondary">{`Abschnitt ${index}`}</Typography>
                <Typography component="div" id={`sektion-${index}`}>
                  {type === 'text' && <Latex>{text}</Latex>}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button onClick={onToggleShowReferences} variant="outlined">
            Referenzen {showReferences ? 'ausblenden' : 'anzeigen'}
          </Button>
          {showReferences && (
            <Box sx={{ my: 4 }}>
              {references.map((reference) => (
                <Typography key={reference}>
                  <Latex>{reference}</Latex>
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <Box sx={{ mt: 4 }}>
          <Questions exercise={exercise} />
        </Box>
      </TabPanel>
    </Box>
  );
}
